import { useUser } from '@src/hooks';
import { useCustomersContext } from '@src/hooks/useCustomer';
import {
  ButtonVariants,
  Dialog,
  IconName,
  isTalosUser,
  NotificationVariants,
  useCallbackRef,
  useDisclosure,
  useGlobalToasts,
  type AgGridMenuItems,
  type CustomerUser,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-enterprise';
import { useMemo, useState, type ReactNode } from 'react';

const CLEAR_USER_CONFIG_MENU_LABEL = 'Clear User Config (Talos Only)';

function getCustomerUserConfigKeys(customerUser: CustomerUser): string[] {
  return [
    ...new Set(
      (customerUser.Config ?? [])
        .map(config => config.Key)
        .filter((key): key is string => key != null && key.length > 0)
    ),
  ];
}

export function useCustomerUsersContextMenu(): {
  getContextMenuItems: (params: GetContextMenuItemsParams<CustomerUser>) => AgGridMenuItems<CustomerUser>;
  dialogs: ReactNode[];
} {
  const user = useUser();
  const isCurrentUserTalos = isTalosUser(user);
  const customersService = useCustomersContext();
  const { add: addToast } = useGlobalToasts();
  const clearUserConfigDialog = useDisclosure();
  const [customerUserToClear, setCustomerUserToClear] = useState<CustomerUser | undefined>();

  const handleClearUserConfig = useCallbackRef(async (customerUser: CustomerUser) => {
    const displayName = customerUser.DisplayName || customerUser.Email || customerUser.CustomerUserID;
    const configKeys = getCustomerUserConfigKeys(customerUser);

    if (configKeys.length === 0) {
      addToast({
        text: `No config keys found for ${displayName}.`,
        variant: NotificationVariants.Warning,
      });
      return;
    }

    const settledResults = await Promise.allSettled(
      configKeys.map(configKey => customersService.removeCustomerUserConfig(customerUser.CustomerUserID, configKey))
    );

    const fulfilledCount = settledResults.filter(result => result.status === 'fulfilled').length;
    const rejectedCount = settledResults.length - fulfilledCount;
    const totalCount = settledResults.length;

    const variant =
      rejectedCount === 0
        ? NotificationVariants.Positive
        : fulfilledCount === 0
          ? NotificationVariants.Negative
          : NotificationVariants.Warning;

    addToast({
      text: `Clear User Config for ${displayName}: ${fulfilledCount}/${totalCount} succeeded, ${rejectedCount} failed.`,
      variant,
    });
  });

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CustomerUser>) => {
    const clickedCustomerUser = params.node?.data;
    const items: AgGridMenuItems<CustomerUser> = [];

    if (clickedCustomerUser != null && isCurrentUserTalos) {
      items.push({
        name: CLEAR_USER_CONFIG_MENU_LABEL,
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
        action: () => {
          setCustomerUserToClear(clickedCustomerUser);
          clearUserConfigDialog.open();
        },
      });
    }

    return items;
  });

  const dialogs = useMemo(
    () => [
      <Dialog
        key="clear-user-config-dialog"
        {...clearUserConfigDialog}
        title={CLEAR_USER_CONFIG_MENU_LABEL}
        showClose={true}
        confirmLabel="Clear Config"
        variant={ButtonVariants.Negative}
        confirmDisabled={customerUserToClear == null}
        stretchButtons
        onConfirm={async () => {
          if (customerUserToClear == null) {
            return;
          }
          await handleClearUserConfig(customerUserToClear);
          clearUserConfigDialog.close();
          setCustomerUserToClear(undefined);
        }}
        onCloseComplete={() => {
          clearUserConfigDialog.close();
          setCustomerUserToClear(undefined);
        }}
      >
        {`This will clear all config keys for ${
          customerUserToClear?.DisplayName || customerUserToClear?.Email || 'the selected user'
        }. Continue?`}
      </Dialog>,
    ],
    [clearUserConfigDialog, customerUserToClear, handleClearUserConfig]
  );

  return {
    getContextMenuItems,
    dialogs,
  };
}
